import React from 'react';
import { motion } from 'framer-motion';
import { FiCode, FiShoppingCart, FiGlobe, FiSmartphone, FiCheckCircle, FiArrowRight, FiZap } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const ITServicesPage = () => {
  const services = [
    {
      id: 'custom-web-development',
      title: 'Custom Web Development',
      icon: FiCode,
      description: 'Tailored web solutions built from scratch to match your unique business requirements and goals.',
      features: [
        'Custom CMS Development',
        'Web Application Development',
        'API Integration',
        'Database Design',
        'Performance Optimization',
        'Security Implementation'
      ],
      pricing: {
        basic: { price: '₹50,000', duration: 'one-time', features: ['5 Pages', 'Responsive Design', 'Basic SEO', '3 Months Support'] },
        professional: { price: '₹1,50,000', duration: 'one-time', features: ['15 Pages', 'Custom Features', 'Advanced SEO', 'CMS Integration', '6 Months Support'] },
        enterprise: { price: 'Custom', duration: 'pricing', features: ['Unlimited Pages', 'Complex Functionality', 'Full Stack Development', 'Dedicated Team', '12 Months Support'] }
      }
    },
    {
      id: 'wordpress',
      title: 'WordPress Development',
      icon: FiGlobe,
      description: 'Professional WordPress websites with custom themes, plugins, and seamless content management.',
      features: [
        'Custom Theme Development',
        'Plugin Customization',
        'WooCommerce Integration',
        'Speed Optimization',
        'Security Hardening',
        'Maintenance & Updates'
      ],
      pricing: {
        basic: { price: '₹25,000', duration: 'one-time', features: ['Pre-built Theme', '5 Pages', 'Basic Plugins', '2 Months Support'] },
        professional: { price: '₹75,000', duration: 'one-time', features: ['Custom Theme', '15 Pages', 'Premium Plugins', 'E-commerce Ready', '6 Months Support'] },
        enterprise: { price: 'Custom', duration: 'pricing', features: ['Fully Custom', 'Advanced Features', 'Multi-site Setup', 'Priority Support', '12 Months Support'] }
      }
    },
    {
      id: 'shopify',
      title: 'Shopify Development',
      description: 'Complete e-commerce solutions on Shopify with custom themes and seamless payment integration.',
      icon: FiShoppingCart,
      features: [
        'Store Setup & Configuration',
        'Custom Theme Design',
        'Payment Gateway Integration',
        'Product Management',
        'Inventory Tracking',
        'Marketing Integration'
      ],
      pricing: {
        basic: { price: '₹35,000', duration: 'one-time', features: ['Store Setup', 'Theme Customization', '50 Products', 'Payment Integration'] },
        professional: { price: '₹85,000', duration: 'one-time', features: ['Custom Design', '200 Products', 'Advanced Features', 'Marketing Tools', 'Training'] },
        enterprise: { price: 'Custom', duration: 'pricing', features: ['Shopify Plus', 'Unlimited Products', 'Custom Apps', 'Multi-Currency', 'Dedicated Support'] }
      }
    },
    {
      id: 'app-development',
      title: 'Mobile App Development',
      icon: FiSmartphone,
      description: 'Native and cross-platform mobile applications for iOS and Android devices.',
      features: [
        'iOS & Android Apps',
        'Cross-Platform Development',
        'UI/UX Design',
        'API Integration',
        'App Store Deployment',
        'Maintenance & Updates'
      ],
      pricing: {
        basic: { price: '₹1,50,000', duration: 'one-time', features: ['Single Platform', '5 Screens', 'Basic Features', '3 Months Support'] },
        professional: { price: '₹3,50,000', duration: 'one-time', features: ['iOS & Android', '15 Screens', 'Advanced Features', 'Backend API', '6 Months Support'] },
        enterprise: { price: 'Custom', duration: 'pricing', features: ['Complex Apps', 'Custom Backend', 'Real-time Features', 'Cloud Integration', '12 Months Support'] }
      }
    },
    {
      id: 'whatsapp-api',
      title: 'WhatsApp API Integration',
      icon: FiZap,
      description: 'Automate customer communication and support with WhatsApp Business API integration.',
      features: [
        'WhatsApp Business API Setup',
        'Chatbot Development',
        'Automated Messaging',
        'CRM Integration',
        'Broadcast Campaigns',
        'Analytics Dashboard'
      ],
      pricing: {
        basic: { price: '₹40,000', duration: 'one-time', features: ['API Setup', 'Basic Automation', '1000 Messages/month', '3 Months Support'] },
        professional: { price: '₹1,00,000', duration: 'one-time', features: ['Custom Chatbot', 'Advanced Automation', '10K Messages/month', 'CRM Integration'] },
        enterprise: { price: 'Custom', duration: 'pricing', features: ['Enterprise Solution', 'Unlimited Messages', 'Multi-Agent Support', 'Custom Integrations'] }
      }
    }
  ];

  const benefits = [
    { icon: FiCode, title: 'Custom Solutions', description: 'Tailored to your specific business needs' },
    { icon: FiZap, title: 'Modern Technology', description: 'Built with latest frameworks and best practices' },
    { icon: FiCheckCircle, title: 'Ongoing Support', description: 'Continuous maintenance and updates' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container-width relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              IT Services & Web Development
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Transform your digital presence with custom web and mobile solutions built for performance and growth
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <span>Get Started</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-width">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-blue-100 text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 transform group-hover:scale-110"
                  style={{
                    boxShadow: '0 10px 30px rgba(37, 99, 235, 0.2), inset -2px -2px 8px rgba(0, 0, 0, 0.1), inset 2px 2px 8px rgba(255, 255, 255, 0.8)'
                  }}
                >
                  <benefit.icon className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      {services.map((service, serviceIndex) => (
        <section key={service.id} className={`py-20 ${serviceIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16 text-center"
            >
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500 to-indigo-700 text-white mb-6"
                style={{
                  boxShadow: '0 20px 40px rgba(37, 99, 235, 0.3), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.3)'
                }}
              >
                <service.icon className="w-12 h-12" />
              </div>
              <h2 className="text-4xl font-bold text-gray-800 mb-4">{service.title}</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">{service.description}</p>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
            >
              {service.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center space-x-3 p-4 rounded-xl bg-white border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 group"
                  style={{
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05), inset -1px -1px 3px rgba(0, 0, 0, 0.05), inset 1px 1px 3px rgba(255, 255, 255, 0.8)'
                  }}
                >
                  <FiCheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="text-gray-700 font-medium">{feature}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {Object.entries(service.pricing).map(([tier, details], index) => (
                <motion.div
                  key={tier}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative p-8 rounded-2xl ${
                    tier === 'professional'
                      ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white transform scale-105 shadow-2xl'
                      : 'bg-white border-2 border-gray-200 hover:border-blue-300'
                  } transition-all duration-300 group hover:shadow-xl`}
                  style={{
                    boxShadow: tier === 'professional'
                      ? '0 20px 60px rgba(37, 99, 235, 0.4), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.2)'
                      : '0 10px 30px rgba(0, 0, 0, 0.08), inset -2px -2px 6px rgba(0, 0, 0, 0.05), inset 2px 2px 6px rgba(255, 255, 255, 0.8)'
                  }}
                >
                  {tier === 'professional' && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Popular Choice
                    </div>
                  )}
                  
                  <h3 className={`text-2xl font-bold mb-2 capitalize ${tier === 'professional' ? 'text-white' : 'text-gray-800'}`}>
                    {tier}
                  </h3>
                  
                  <div className="mb-6">
                    <span className={`text-5xl font-bold ${tier === 'professional' ? 'text-white' : 'text-blue-600'}`}>
                      {details.price}
                    </span>
                    <span className={`text-lg ${tier === 'professional' ? 'text-white/80' : 'text-gray-500'}`}>
                      {' '}{details.duration}
                    </span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {details.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <FiCheckCircle className={`w-5 h-5 flex-shrink-0 ${tier === 'professional' ? 'text-green-300' : 'text-green-500'}`} />
                        <span className={tier === 'professional' ? 'text-white/90' : 'text-gray-600'}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className={`block w-full text-center py-3 rounded-full font-semibold transition-all duration-300 ${
                      tier === 'professional'
                        ? 'bg-white text-blue-600 hover:bg-orange-500 hover:text-white'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Get Started
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="container-width text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Build Something Amazing?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's bring your vision to life with cutting-edge web and mobile solutions
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <span>Start Your Project</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ITServicesPage;
